import { useCallback, useEffect, useRef, useState } from 'react'
import { adminMutate, apiRead } from '../admin/primitives.js'
import { messageErreur } from './tablettes-modele.js'

/**
 * TABLETTES — lecture périodique et gestes (navigateur seulement). Le modèle
 * pur (tablettes-modele.ts) reste testable sans React.
 */

const estObjet = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)

export type Lecture<T> = { etat: 'ok'; data: T; le: number } | { etat: 'erreur'; status: number; code: string | null; message: string }

/**
 * Lit une route du module à intervalle, en pause quand l'onglet est caché.
 * Garde la dernière bonne lecture pendant une panne (`precedente`).
 */
export function useLectureTablettes<T>(chemin: string | null, valide: (x: unknown) => x is T, intervalMs: number): {
  lecture: Lecture<T> | null; precedente: T | null; recharger: () => void
} {
  const [lecture, setLecture] = useState<Lecture<T> | null>(null)
  const precedente = useRef<T | null>(null)
  const [tic, setTic] = useState(0)
  const recharger = useCallback(() => setTic((n) => n + 1), [])
  useEffect(() => {
    if (!chemin) return
    let vivant = true
    let minuteur: ReturnType<typeof setTimeout> | null = null
    const lire = async () => {
      try {
        const r = await apiRead<unknown>(chemin)
        if (!vivant) return
        if (r.status === 200 && valide(r.data)) {
          precedente.current = r.data
          setLecture({ etat: 'ok', data: r.data, le: Date.now() })
        } else {
          const code = estObjet(r.data) && typeof r.data['error'] === 'string' ? r.data['error'] : null
          setLecture({ etat: 'erreur', status: r.status, code, message: messageErreur(r.status, r.data, 'Lecture impossible.') })
        }
      } catch {
        if (vivant) setLecture({ etat: 'erreur', status: 0, code: null, message: 'Gestion n’a pas répondu.' })
      }
      if (vivant) minuteur = setTimeout(boucle, intervalMs)
    }
    const boucle = () => { if (document.hidden) { minuteur = setTimeout(boucle, intervalMs); return } void lire() }
    void lire()
    const visible = () => { if (!document.hidden && minuteur) { clearTimeout(minuteur); void lire() } }
    document.addEventListener('visibilitychange', visible)
    return () => { vivant = false; if (minuteur) clearTimeout(minuteur); document.removeEventListener('visibilitychange', visible) }
  }, [chemin, intervalMs, tic]) // eslint-disable-line react-hooks/exhaustive-deps
  return { lecture, precedente: precedente.current, recharger }
}

export interface Issue { ok: boolean; message: string; rejoue?: boolean }

/**
 * Un geste vers les tablettes. La confirmation forte est demandée par
 * `adminMutate` si le serveur l'exige ; une clé d'idempotence par geste (un
 * double clic ne redémarre pas deux fois).
 */
export async function geste(method: 'POST' | 'PATCH' | 'DELETE', chemin: string, corps?: unknown, cle?: string): Promise<Issue & { data?: Record<string, unknown> }> {
  try {
    const r = await adminMutate<Record<string, unknown> & { error?: string }>(method, chemin, corps,
      { headers: { 'Idempotency-Key': cle ?? crypto.randomUUID() }, deconnexionExplicite: true })
    if (r.status >= 200 && r.status < 300) return { ok: true, message: r.status === 202 ? 'Envoyé : la tablette le recevra à son retour.' : 'Fait.', rejoue: r.data['rejoue'] === true, data: r.data }
    if (r.data.error === 'STEPUP_REQUIRED') return { ok: false, message: 'Confirmation d’identité annulée : rien n’a été envoyé.' }
    return { ok: false, message: messageErreur(r.status, r.data, 'Le geste n’a pas abouti.') }
  } catch {
    return { ok: false, message: 'Gestion n’a pas répondu : regardez l’état de la tablette avant de recommencer.' }
  }
}
